import React, { useEffect, useCallback } from 'react';
import { Anchor, Box, Button, Card, Image, Text } from 'grommet';
import { StatusDisabled, StatusGood } from 'grommet-icons';
import Skeleton from 'react-loading-skeleton';
import SportsRecruitsAuthorizationModal from './SportsRecruitsAuthorizationModal';
import { ClaimedPlayer } from '@features/Home/claimedPlayer.d';
import useSportsRecruits from './useSportsRecruits';
import { useTray } from '@contexts/TrayContext';

const SRIntegration: React.FC<{ claimedPlayer: ClaimedPlayer | undefined }> = ({
  claimedPlayer,
}) => {
  const { showTray, hideTray } = useTray();
  const showSRTray = useCallback(() => {
    showTray(
      <SportsRecruitsAuthorizationModal
        onClose={() => {
          hideTray();
        }}
        playerId={claimedPlayer?.id}
        fromDeepLink={false}
      />,
      {
        onClose: () => {
          hideTray();
        },
      }
    );
  }, [claimedPlayer?.id, hideTray, showTray]);

  const { fetchSRPlayer, linkedPlayerProfile, loading, error } =
    useSportsRecruits(claimedPlayer?.id);

  useEffect(() => {
    fetchSRPlayer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderSRStatus = (isLinked: boolean) => {
    return (
      <Box
        direction="row"
        align="center"
        gap="small"
        pad="xsmall"
        background={isLinked ? 'status-ok' : 'light-3'}
        border
        round
      >
        {isLinked && (
          <>
            <StatusGood />
            <Text>Connected</Text>
          </>
        )}
        {!isLinked && (
          <>
            <StatusDisabled />
            <Text>Not Connected</Text>
          </>
        )}
      </Box>
    );
  };

  const renderLinked = () => (
    <Box gap="medium">
      <Box direction="row" gap="small" align="center">
        <Text>Linked to: </Text>
        <Anchor
          href={linkedPlayerProfile?.profile_url}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Text>{linkedPlayerProfile?.profile_url}</Text>
        </Anchor>
        <Text>{error && error}</Text>
      </Box>
    </Box>
  );

  const renderUnlinked = () => (
    <Box gap="medium">
      <Box direction="row" gap="small" align="center">
        <Button
          primary
          color="tertiary-1"
          label="Connect to SportsRecruits"
          onClick={() => {
            showSRTray();
          }}
          fill="horizontal"
        />
      </Box>
    </Box>
  );

  return (
    <Card
      elevation="none"
      pad="small"
      gap="small"
      border={{ color: 'dark-5', size: '2px' }}
    >
      <Box direction="row" gap="small">
        <Image src="/sr_logo.png" width="24px" />
        <Text size="large" weight="bold">
          SportsRecruits
        </Text>
      </Box>
      {renderSRStatus(!!linkedPlayerProfile)}
      {loading ? (
        <Skeleton height={44} />
      ) : linkedPlayerProfile ? (
        renderLinked()
      ) : (
        renderUnlinked()
      )}
    </Card>
  );
};

export default SRIntegration;
